import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import {
  CALISMA_ETIKETI,
  GUN_KISALTMASI,
  KALIBRASYON_ETIKETI,
  TEKRAR_ETIKETI,
  gorevSablonlari,
  gorevTipiTanimla,
  gorevTipleri,
  kalibrasyonOnerileri,
  kalibreEt,
  sablonPasifeAl,
  sablonTanimla,
  type CalismaBicimi,
  type GorevTipiOzeti,
  type KalibrasyonSatiri,
  type Koordinat,
  type SablonOzeti,
  type Tekrar,
} from '../api/gorev';
import type { Kimlik } from '../api/tipler';
import { BirimSecici } from '../bilesenler/BirimSecici';
import { Diyalog, GerekceDiyalogu } from '../bilesenler/Diyalog';
import { HaritaNoktasi } from '../bilesenler/HaritaNoktasi';
import { Tablo, type Sutun } from '../bilesenler/Tablo';
import { CokSatir, Dugme, HataKutusu, Metin } from '../bilesenler/temel';
import { yetkiKullan } from '../yetki/yetki';

/**
 * GÖREV TİPLERİ VE ŞABLONLAR.
 *
 * KALİBRASYON OTOMATİK UYGULANMAZ. Sistem öneri üretir, müdür kabul eder.
 * Kendiliğinden uygulansaydı ölçtüğü şeyin peşinden koşan bir gösterge olurdu:
 * ekip yavaşladıkça "beklenen süre" de uzar ve sapma hep sıfır görünürdü.
 *
 * `veri_tutarsiz` DURUMUNDA ÖNERİ DÜĞMESİ GÖSTERİLMEZ: std sapma/ortalama
 * %30'u aşmışsa dağınık veriden çıkan ortalama yanıltıcıdır. Eşik SUNUCUDA
 * hesaplanıyor; `stdSapmaOrani` ondalık METİN olduğu için panelde sayıya
 * çevrilip karşılaştırılmıyor — `durum` alanı kullanılıyor.
 */

export function GorevTipleri() {
  const yonetebilir = yetkiKullan('GOREV_TIPI_YONET');

  const [birimId, birimIdYaz] = useState<Kimlik | undefined>(undefined);
  const [formAcik, formAcikYaz] = useState(false);

  const tipler = useQuery({
    queryKey: ['gorev', 'tip', birimId],
    queryFn: () => gorevTipleri(birimId),
  });

  const tipSutunlari: Sutun<GorevTipiOzeti>[] = [
    {
      baslik: 'Tip',
      hucre: (t) => (
        <span>
          <span className="block font-medium text-slate-900">{t.ad}</span>
          <span className="block text-xs text-slate-500">{t.kod}</span>
        </span>
      ),
    },
    {
      baslik: 'Beklenen',
      hucre: (t) => (
        <span className="whitespace-nowrap text-xs">
          {t.beklenenSureDk === null ? '—' : `${t.beklenenSureDk} dk`}
          {t.beklenenPersonel !== null && ` × ${t.beklenenPersonel}`}
        </span>
      ),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'İşgücü',
      // `fn_gorev_beklenen_isgucu` — birlikte: süre × kişi, dönüşümlü: süre.
      hucre: (t) => (t.beklenenIsgucuDk === null ? '—' : `${t.beklenenIsgucuDk} dk`),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Çalışma',
      hucre: (t) => (t.calismaBicimi === 'birlikte' ? 'Birlikte' : 'Dönüşümlü'),
      daralt: true,
    },
    {
      baslik: 'Durum',
      hucre: (t) =>
        t.aktif ? (
          <span className="text-xs text-slate-500">Aktif</span>
        ) : (
          /* Tip SİLİNMEZ, pasife alınır. */
          <span className="whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
            Pasif
          </span>
        ),
      daralt: true,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="w-64">
          <BirimSecici deger={birimId} degisti={birimIdYaz} bosSecenek="Tüm birimler" />
        </div>
        {yonetebilir && <Dugme tikla={() => formAcikYaz(true)} cocuk="Görev tipi tanımla" />}
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-slate-800">Görev tipleri</h2>
        <HataKutusu hata={tipler.error} />
        <Tablo
          sutunlar={tipSutunlari}
          satirlar={tipler.data ?? []}
          anahtar={(t) => String(t.gorevTipiId)}
          bekliyor={tipler.isPending}
          bosMetin="Tanımlı görev tipi yok."
        />
      </section>

      {formAcik && <GorevTipiDiyalogu kapat={() => formAcikYaz(false)} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Kalibrasyon bölümü — RAPORLAR sekmesinde                            */
/* ------------------------------------------------------------------ */

/**
 * KALİBRASYON BÖLÜMÜ RAPORLAR SEKMESİNDE DURUR, GÖREV TİPLERİNDE DEĞİL.
 *
 * `GET /gorev/kalibrasyon` `GOREV_TIPI_YONET | RAPOR_GORUNTULE` kabul ediyor;
 * `GET /gorev/tip` ise `RAPOR_GORUNTULE`yi KABUL ETMİYOR. Bölüm tip sekmesinde
 * bırakılsaydı yalnız `RAPOR_GORUNTULE` olan kullanıcı erişebildiği veriyi hiç
 * göremezdi — SÖZLEŞME §11'in "menü uçtan dar olmamalı" kuralı.
 *
 * Raporlar sekmesinin yetkisi (`GOREV_TIPI_YONET | RAPOR_GORUNTULE`) bu ucun
 * listesiyle BİREBİR aynı.
 */
export function KalibrasyonBolumu({ birimId }: { birimId?: Kimlik }) {
  const yonetebilir = yetkiKullan('GOREV_TIPI_YONET');
  const [kalibreEdilen, kalibreEdilenYaz] = useState<KalibrasyonSatiri | null>(null);

  const kalibrasyon = useQuery({
    queryKey: ['gorev', 'kalibrasyon', birimId],
    queryFn: () => kalibrasyonOnerileri(birimId),
  });

  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold text-slate-800">Kalibrasyon önerileri</h2>
      <p className="text-xs text-slate-500">
        10-29 kayıt → ön kalibrasyon (düşük güven) · 30+ → kalibre edilebilir. Veri dağınıksa (std
        sapma/ortalama &gt; %30) öneri gösterilmez.
      </p>
      <HataKutusu hata={kalibrasyon.error} />
      <KalibrasyonTablosu
        satirlar={kalibrasyon.data ?? []}
        bekliyor={kalibrasyon.isPending}
        yonetebilir={yonetebilir}
        kabulEt={kalibreEdilenYaz}
      />

      {kalibreEdilen !== null && (
        <KalibrasyonDiyalogu satir={kalibreEdilen} kapat={() => kalibreEdilenYaz(null)} />
      )}
    </section>
  );
}

/* ------------------------------------------------------------------ */

function KalibrasyonTablosu({
  satirlar,
  bekliyor,
  yonetebilir,
  kabulEt,
}: {
  satirlar: KalibrasyonSatiri[];
  bekliyor: boolean;
  yonetebilir: boolean;
  kabulEt: (satir: KalibrasyonSatiri) => void;
}) {
  const sutunlar: Sutun<KalibrasyonSatiri>[] = [
    {
      baslik: 'Tip',
      hucre: (k) => (
        <span>
          <span className="block font-medium text-slate-900">{k.gorevTipiAd}</span>
          <span className="block text-xs text-slate-500">{k.gorevTipiKod}</span>
        </span>
      ),
    },
    {
      baslik: 'Tanımlı',
      hucre: (k) => (
        <span className="whitespace-nowrap text-xs">
          {k.tanimliSureDk === null ? '—' : `${k.tanimliSureDk} dk`}
          {k.tanimliPersonel !== null && ` × ${k.tanimliPersonel}`}
        </span>
      ),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Ölçülen',
      hucre: (k) => (
        <span className="whitespace-nowrap text-xs">
          {k.fiiliOrtSureDk === null ? '—' : `${k.fiiliOrtSureDk} dk`}
          {/* Ondalık METİN — sayıya çevrilmez. */}
          {k.fiiliOrtPersonel !== null && ` × ${k.fiiliOrtPersonel}`}
        </span>
      ),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Kayıt',
      hucre: (k) => (k.kayitSayisi === null ? '—' : String(k.kayitSayisi)),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Dağılım',
      hucre: (k) =>
        k.stdSapmaOrani === null ? (
          '—'
        ) : (
          <span className="whitespace-nowrap text-xs">
            ±{k.stdSapmaDk ?? '—'} dk · {k.stdSapmaOrani}
          </span>
        ),
      daralt: true,
      hizala: 'sag',
    },
    {
      baslik: 'Durum',
      hucre: (k) =>
        k.durum === null ? (
          '—'
        ) : (
          <span
            className={`whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${
              k.durum === 'kalibre_edilebilir'
                ? 'bg-emerald-50 text-emerald-700'
                : k.durum === 'veri_tutarsiz'
                  ? 'bg-red-50 text-red-700'
                  : 'bg-slate-100 text-slate-600'
            }`}
          >
            {KALIBRASYON_ETIKETI[k.durum]}
          </span>
        ),
      daralt: true,
    },
    {
      baslik: '',
      hucre: (k) =>
        /*
          ÖNERİ DÜĞMESİ YALNIZ VERİ SAĞLAMSA. `veri_tutarsiz` ve
          `veri_yetersiz` durumlarında dağınık ya da az veriden çıkan
          ortalama yanıltıcıdır; düğme gösterilseydi müdür ona güvenirdi.
        */
        yonetebilir &&
        k.kalibrasyonGerekliMi &&
        (k.durum === 'kalibre_edilebilir' || k.durum === 'on_kalibrasyon') ? (
          <button
            type="button"
            onClick={() => kabulEt(k)}
            className="whitespace-nowrap rounded px-2 py-1 text-xs text-kurum-700 hover:bg-kurum-50"
          >
            Öneriyi uygula
          </button>
        ) : null,
      hizala: 'sag',
      daralt: true,
    },
  ];

  return (
    <Tablo
      sutunlar={sutunlar}
      satirlar={satirlar}
      anahtar={(k) => String(k.gorevTipiId)}
      bekliyor={bekliyor}
      bosMetin="Kalibrasyon önerisi yok."
    />
  );
}

function KalibrasyonDiyalogu({ satir, kapat }: { satir: KalibrasyonSatiri; kapat: () => void }) {
  const sorguIstemcisi = useQueryClient();

  const uygula = useMutation({
    mutationFn: (gerekce: string) =>
      kalibreEt(
        satir.gorevTipiId,
        satir.fiiliOrtSureDk ?? 0,
        // Ondalık ortalama personel TAM SAYIYA yuvarlanır: sunucu `@IsInt`
        // istiyor. En az 1 — sıfır kişi anlamsız.
        Math.max(1, Math.round(Number(satir.fiiliOrtPersonel ?? '1'))),
        gerekce === '' ? undefined : gerekce,
      ),
    onSuccess: async () => {
      await sorguIstemcisi.invalidateQueries({ queryKey: ['gorev'] });
      kapat();
    },
  });

  return (
    <GerekceDiyalogu
      baslik={`${satir.gorevTipiAd} — öneriyi uygula`}
      aciklama={`Tanımlı ${satir.tanimliSureDk ?? '—'} dk → ölçülen ${satir.fiiliOrtSureDk ?? '—'} dk · ${satir.kayitSayisi ?? 0} kayıt`}
      etiket="Gerekçe"
      // `KalibreEtDto.gerekce` @IsOptional — iptal ve pasife almanın aksine.
      zorunluMu={false}
      ipucu="İsteğe bağlı — denetim izine yazılır."
      onayEtiketi="Uygula"
      enCokKarakter={500}
      bekliyor={uygula.isPending}
      hata={uygula.error}
      kapat={kapat}
      onayla={(gerekce) => uygula.mutate(gerekce)}
    />
  );
}

/* ------------------------------------------------------------------ */

function GorevTipiDiyalogu({ kapat }: { kapat: () => void }) {
  const sorguIstemcisi = useQueryClient();

  const [kod, kodYaz] = useState('');
  const [ad, adYaz] = useState('');
  const [birimId, birimIdYaz] = useState<Kimlik | undefined>(undefined);
  const [beklenenSureDk, beklenenSureDkYaz] = useState('');
  const [beklenenPersonel, beklenenPersonelYaz] = useState('');
  const [calismaBicimi, calismaBicimiYaz] = useState<CalismaBicimi>('birlikte');
  const [aciklama, aciklamaYaz] = useState('');

  const tanimla = useMutation({
    mutationFn: () =>
      gorevTipiTanimla({
        kod: kod.trim().toLocaleUpperCase('tr-TR'),
        ad: ad.trim(),
        birimId: birimId!,
        beklenenSureDk: beklenenSureDk === '' ? undefined : Number(beklenenSureDk),
        beklenenPersonel: beklenenPersonel === '' ? undefined : Number(beklenenPersonel),
        calismaBicimi,
        aciklama: aciklama.trim() === '' ? undefined : aciklama.trim(),
      }),
    onSuccess: async () => {
      await sorguIstemcisi.invalidateQueries({ queryKey: ['gorev'] });
      kapat();
    },
  });

  const gonderilebilir = kod.trim() !== '' && ad.trim() !== '' && birimId !== undefined;

  return (
    <Diyalog
      baslik="Görev tipi tanımla"
      aciklama="İlk süre tahmindir; sistem 10 kayıttan sonra gerçek ortalamayı önerir."
      kapat={kapat}
      genis
      cocuk={
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <Metin etiket="Kod" deger={kod} degisti={kodYaz} ipucu="Örn. CADDE_SUPURME" />
            <Metin etiket="Ad" deger={ad} degisti={adYaz} />
          </div>

          <BirimSecici deger={birimId} degisti={birimIdYaz} />

          <div className="grid gap-4 sm:grid-cols-2">
            <Metin
              etiket="Beklenen süre (dk)"
              deger={beklenenSureDk}
              degisti={beklenenSureDkYaz}
              ipucu="Beklenen personel ile BİRLİKTE girilir"
            />
            <Metin
              etiket="Beklenen personel"
              deger={beklenenPersonel}
              degisti={beklenenPersonelYaz}
            />
          </div>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Çalışma biçimi</span>
            <select
              value={calismaBicimi}
              onChange={(olay) => calismaBicimiYaz(olay.target.value as CalismaBicimi)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
            >
              {(Object.keys(CALISMA_ETIKETI) as CalismaBicimi[]).map((c) => (
                <option key={c} value={c}>
                  {CALISMA_ETIKETI[c]}
                </option>
              ))}
            </select>
          </label>

          {/*
            `kanitZorunluMu` FORMDA YOK. Faz 1'de dosya yükleme yok ve trigger
            foto olmadan görevi bitirmeye izin vermiyor; alan açılsaydı
            işaretlenen tip bitirilemez hâle gelirdi.
          */}

          <CokSatir
            etiket="Açıklama (isteğe bağlı)"
            deger={aciklama}
            degisti={aciklamaYaz}
            satir={2}
          />

          <HataKutusu hata={tanimla.error} />

          <div className="flex justify-end gap-2">
            <Dugme tur="ikincil" tikla={kapat} pasif={tanimla.isPending} cocuk="Vazgeç" />
            <Dugme
              tikla={() => tanimla.mutate()}
              bekliyor={tanimla.isPending}
              pasif={!gonderilebilir}
              cocuk="Tanımla"
            />
          </div>
        </div>
      }
    />
  );
}

/* ------------------------------------------------------------------ */
/* Şablonlar                                                           */
/* ------------------------------------------------------------------ */

export function GorevSablonlari() {
  const sorguIstemcisi = useQueryClient();
  const yonetebilir = yetkiKullan('GOREV_TIPI_YONET');

  const [birimId, birimIdYaz] = useState<Kimlik | undefined>(undefined);
  const [formAcik, formAcikYaz] = useState(false);
  const [pasifeAlinan, pasifeAlinanYaz] = useState<SablonOzeti | null>(null);

  const sablonlar = useQuery({
    queryKey: ['gorev', 'sablon', birimId],
    queryFn: () => gorevSablonlari(birimId),
  });

  const pasifeAl = useMutation({
    mutationFn: (girdi: { id: Kimlik; gerekce: string }) => sablonPasifeAl(girdi.id, girdi.gerekce),
    onSuccess: async () => {
      await sorguIstemcisi.invalidateQueries({ queryKey: ['gorev'] });
      pasifeAlinanYaz(null);
    },
  });

  const sutunlar: Sutun<SablonOzeti>[] = [
    {
      baslik: 'Görev tipi',
      hucre: (s) => (
        <span>
          <span className="block font-medium text-slate-900">{s.gorevTipiAd}</span>
          <span className="block text-xs text-slate-500">
            {s.hedefBirimAd}
            {s.lokasyonMetni !== null && ` · ${s.lokasyonMetni}`}
          </span>
        </span>
      ),
    },
    { baslik: 'Tekrar', hucre: (s) => <TekrarMetni sablon={s} />, daralt: true },
    { baslik: 'Öncelik', hucre: (s) => String(s.oncelik), daralt: true, hizala: 'sag' },
    {
      baslik: 'Durum',
      hucre: (s) =>
        s.aktif ? (
          <span className="text-xs text-slate-500">Aktif</span>
        ) : (
          <span className="whitespace-nowrap rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
            Pasif
          </span>
        ),
      daralt: true,
    },
    {
      baslik: '',
      hucre: (s) =>
        yonetebilir && s.aktif ? (
          <button
            type="button"
            onClick={() => pasifeAlinanYaz(s)}
            className="whitespace-nowrap rounded px-2 py-1 text-xs text-red-700 hover:bg-red-50"
          >
            Pasife al
          </button>
        ) : null,
      hizala: 'sag',
      daralt: true,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="w-64">
          <BirimSecici deger={birimId} degisti={birimIdYaz} bosSecenek="Tüm birimler" />
        </div>
        {yonetebilir && <Dugme tikla={() => formAcikYaz(true)} cocuk="Şablon tanımla" />}
      </div>

      <p className="text-xs text-slate-500">
        Şablon periyodik iş içindir. TEK SEFERLİK İŞ ŞABLONLA YAPILMAZ — doğrudan görev açılır.
      </p>

      <HataKutusu hata={sablonlar.error} />

      <Tablo
        sutunlar={sutunlar}
        satirlar={sablonlar.data ?? []}
        anahtar={(s) => s.sablonId}
        bekliyor={sablonlar.isPending}
        bosMetin="Tanımlı şablon yok."
      />

      {formAcik && <SablonDiyalogu kapat={() => formAcikYaz(false)} />}

      {pasifeAlinan !== null && (
        <GerekceDiyalogu
          baslik={`${pasifeAlinan.gorevTipiAd} şablonunu pasife al`}
          aciklama="Şablon SİLİNMEZ, pasife alınır. Üretilmiş görevler etkilenmez."
          etiket="Gerekçe"
          onayEtiketi="Pasife al"
          onayTuru="tehlike"
          enCokKarakter={500}
          bekliyor={pasifeAl.isPending}
          hata={pasifeAl.error}
          kapat={() => pasifeAlinanYaz(null)}
          onayla={(gerekce) => pasifeAl.mutate({ id: pasifeAlinan.sablonId, gerekce })}
        />
      )}
    </div>
  );
}

function TekrarMetni({ sablon }: { sablon: SablonOzeti }) {
  if (sablon.tekrar === 'haftalik') {
    const gunler = (sablon.gunSecimi ?? [])
      .map((g) => GUN_KISALTMASI[g - 1] ?? '')
      .filter((g) => g !== '')
      .join(', ');

    return <span className="whitespace-nowrap text-xs">{gunler === '' ? 'Haftalık' : gunler}</span>;
  }

  if (sablon.tekrar === 'aylik') {
    return <span className="whitespace-nowrap text-xs">Ayın {sablon.ayinGunu ?? '—'}. günü</span>;
  }

  return <span className="whitespace-nowrap text-xs">{TEKRAR_ETIKETI[sablon.tekrar]}</span>;
}

/* ------------------------------------------------------------------ */

function SablonDiyalogu({ kapat }: { kapat: () => void }) {
  const sorguIstemcisi = useQueryClient();

  const [hedefBirimId, hedefBirimIdYaz] = useState<Kimlik | undefined>(undefined);
  const [gorevTipiId, gorevTipiIdYaz] = useState<number | ''>('');
  const [lokasyonMetni, lokasyonMetniYaz] = useState('');
  const [koordinat, koordinatYaz] = useState<Koordinat | null>(null);
  const [tekrar, tekrarYaz] = useState<Tekrar>('gunluk');
  const [gunSecimi, gunSecimiYaz] = useState<number[]>([]);
  const [ayinGunu, ayinGunuYaz] = useState('1');
  const [oncelik, oncelikYaz] = useState('5');
  const [aciklama, aciklamaYaz] = useState('');

  const tipler = useQuery({
    queryKey: ['gorev', 'tip', hedefBirimId],
    queryFn: () => gorevTipleri(hedefBirimId),
  });

  const tanimla = useMutation({
    mutationFn: () =>
      sablonTanimla({
        gorevTipiId: Number(gorevTipiId),
        hedefBirimId: hedefBirimId!,
        lokasyonMetni: lokasyonMetni.trim() === '' ? undefined : lokasyonMetni.trim(),
        koordinat,
        tekrar,
        // İKİSİ DE KOŞULLU: haftalıkta gün seçimi, aylıkta ayın günü ZORUNLU;
        // diğerinde GÖNDERİLMEZ — sunucu ilgisiz alanı reddediyor.
        gunSecimi: tekrar === 'haftalik' ? gunSecimi : undefined,
        ayinGunu: tekrar === 'aylik' ? Number(ayinGunu) : undefined,
        oncelik: Number(oncelik),
        aciklama: aciklama.trim() === '' ? undefined : aciklama.trim(),
      }),
    onSuccess: async () => {
      await sorguIstemcisi.invalidateQueries({ queryKey: ['gorev'] });
      kapat();
    },
  });

  const gunDegistir = (gun: number) => {
    gunSecimiYaz((o) => (o.includes(gun) ? o.filter((g) => g !== gun) : [...o, gun].sort()));
  };

  const gonderilebilir =
    hedefBirimId !== undefined &&
    gorevTipiId !== '' &&
    (tekrar !== 'haftalik' || gunSecimi.length > 0) &&
    (tekrar !== 'aylik' || (Number(ayinGunu) >= 1 && Number(ayinGunu) <= 28));

  return (
    <Diyalog
      baslik="Görev şablonu tanımla"
      aciklama="Periyodik iş için. Tek seferlik iş doğrudan görev açılarak yapılır."
      kapat={kapat}
      genis
      cocuk={
        <div className="space-y-4">
          <BirimSecici deger={hedefBirimId} degisti={hedefBirimIdYaz} etiket="Hedef birim" />

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Görev tipi</span>
            <select
              value={gorevTipiId}
              onChange={(olay) =>
                gorevTipiIdYaz(olay.target.value === '' ? '' : Number(olay.target.value))
              }
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
            >
              <option value="">Seçin…</option>
              {(tipler.data ?? [])
                .filter((t) => t.aktif)
                .map((t) => (
                  <option key={t.gorevTipiId} value={t.gorevTipiId}>
                    {t.ad}
                  </option>
                ))}
            </select>
          </label>

          <Metin
            etiket="Lokasyon (isteğe bağlı)"
            deger={lokasyonMetni}
            degisti={lokasyonMetniYaz}
          />

          <HaritaNoktasi deger={koordinat} degisti={koordinatYaz} />

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">Tekrar</span>
            <select
              value={tekrar}
              onChange={(olay) => tekrarYaz(olay.target.value as Tekrar)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm outline-none focus:border-kurum-500 focus:ring-2 focus:ring-kurum-100"
            >
              {(Object.keys(TEKRAR_ETIKETI) as Tekrar[]).map((t) => (
                <option key={t} value={t}>
                  {TEKRAR_ETIKETI[t]}
                </option>
              ))}
            </select>
          </label>

          {tekrar === 'haftalik' && (
            <div>
              <span className="mb-1 block text-sm font-medium text-slate-700">Günler</span>
              <div className="flex flex-wrap gap-1">
                {GUN_KISALTMASI.map((ad, dizin) => {
                  const gun = dizin + 1;
                  const secili = gunSecimi.includes(gun);

                  return (
                    <button
                      key={gun}
                      type="button"
                      aria-pressed={secili}
                      onClick={() => gunDegistir(gun)}
                      className={`rounded-md border px-3 py-1.5 text-sm ${
                        secili
                          ? 'border-kurum-500 bg-kurum-50 font-medium text-kurum-800'
                          : 'border-slate-300 text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      {ad}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {tekrar === 'aylik' && (
            <Metin
              etiket="Ayın günü (1–28)"
              deger={ayinGunu}
              degisti={ayinGunuYaz}
              // 29-31 KABUL EDİLMEZ: Şubat'ta o günler her yıl yok.
              ipucu="29-31 kabul edilmez — Şubat'ta bulunmayan günler"
            />
          )}

          <Metin etiket="Öncelik (1–9)" deger={oncelik} degisti={oncelikYaz} ipucu="1 en yüksek" />

          <CokSatir
            etiket="Açıklama (isteğe bağlı)"
            deger={aciklama}
            degisti={aciklamaYaz}
            satir={2}
          />

          <HataKutusu hata={tipler.error} />
          <HataKutusu hata={tanimla.error} />

          <div className="flex justify-end gap-2">
            <Dugme tur="ikincil" tikla={kapat} pasif={tanimla.isPending} cocuk="Vazgeç" />
            <Dugme
              tikla={() => tanimla.mutate()}
              bekliyor={tanimla.isPending}
              pasif={!gonderilebilir}
              cocuk="Tanımla"
            />
          </div>
        </div>
      }
    />
  );
}
